import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DollarSign } from "lucide-react";

const STATUS_BADGE = {
  active: "text-emerald-700 bg-emerald-50 border-emerald-200",
  expired: "text-red-700 bg-red-50 border-red-200",
  canceled: "text-gray-700 bg-gray-100 border-gray-200",
};

function statusClass(status) {
  return STATUS_BADGE[status] || "text-amber-800 bg-amber-50 border-amber-200";
}

/** Contractor membership table + 4-tile MRR/ARR/Active/Expiring summary. */
export function MembershipsCard({ memberships, summary }) {
  if (!memberships || memberships.length === 0) return null;
  const active = memberships.filter((m) => m.status === "active").length;
  const expired = memberships.filter((m) => m.status === "expired").length;
  return (
    <Card className="border-[#E5E7EB] mb-10" data-testid="memberships-card">
      <CardHeader>
        <CardTitle className="font-['Outfit'] flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#C25934]" />
          Contractor memberships
          <span className="text-xs text-gray-500 font-normal">
            · {active} active · {expired} expired
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {summary && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
            data-testid="membership-summary-row"
          >
            <SummaryTile
              label="MRR"
              value={`$${summary.mrr_usd.toLocaleString()}`}
              caption={`ARR $${summary.arr_usd.toLocaleString()}`}
              valueTestId="mrr-stat"
            />
            <SummaryTile
              label="Potential MRR"
              value={`$${summary.potential_mrr_usd.toLocaleString()}`}
              caption={
                summary.lift_potential_pct !== null
                  ? `+${summary.lift_potential_pct}% lift if all renew`
                  : "Lift unbounded (no MRR yet)"
              }
              valueTestId="potential-mrr-stat"
              accent
            />
            <SummaryTile
              label="Active members"
              value={
                <>
                  {summary.active}
                  <span className="text-sm text-gray-400 font-normal">
                    {" "}
                    / {summary.total}
                  </span>
                </>
              }
              caption={`${summary.canceled} canceled · ${summary.expired} expired`}
              valueTestId="active-members-stat"
            />
            <SummaryTile
              label="Expiring ≤7d"
              value={summary.expiring_soon_7d}
              caption="Renewal at-risk"
              valueTestId="expiring-soon-stat"
              urgent={summary.expiring_soon_7d > 0}
            />
          </div>
        )}
        <div className="overflow-x-auto">
          <Table data-testid="memberships-table">
            <TableHeader>
              <TableRow>
                <TableHead>Contractor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Days left</TableHead>
                <TableHead>Period ends</TableHead>
                <TableHead>Last paid</TableHead>
                <TableHead className="text-right">Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {memberships.map((m) => (
                <TableRow
                  key={m.id}
                  data-testid={`membership-row-${m.contractor_id}`}
                >
                  <TableCell>
                    <div className="font-medium text-[#1E3F33]">
                      {m.company_name || m.contractor_name || "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {m.contractor_email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${statusClass(m.status)}`}
                    >
                      {m.status}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono ${
                      typeof m.days_left === "number" && m.days_left <= 7
                        ? "text-[#C25934]"
                        : ""
                    }`}
                  >
                    {typeof m.days_left === "number" ? m.days_left : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {m.current_period_end
                      ? m.current_period_end.slice(0, 10)
                      : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {m.last_payment_at ? m.last_payment_at.slice(0, 10) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    ${Number(m.amount_usd || 0).toFixed(0)}/{m.interval_days}d
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryTile({ label, value, caption, valueTestId, accent, urgent }) {
  let valueColor = "text-[#1E3F33]";
  if (accent) valueColor = "text-[#C25934]";
  if (urgent) valueColor = "text-[#C25934]";
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white px-4 py-3">
      <div className="text-xs text-gray-500 uppercase tracking-wide">
        {label}
      </div>
      <div
        className={`text-xl font-['Outfit'] font-semibold mt-0.5 ${valueColor}`}
        data-testid={valueTestId}
      >
        {value}
      </div>
      <div className="text-[10px] text-gray-500 mt-0.5">{caption}</div>
    </div>
  );
}
